import React, { useEffect, useState } from "react";
import { Table, Image, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faBoxOpen, faReceipt } from "@fortawesome/free-solid-svg-icons";
import { useLazyQuery } from "@apollo/client";
import useReactRouter from "use-react-router";
import moment from "moment";

import { COLOR_APP } from "../../constants";
import { thousandSeperate } from "../../helpers";
import { convertOrderStatus } from "../../helpers/orderStatus";
import { GET_CUSTOMER_ORDER_HISTORY } from "../../graphql/customer/query";
import LoadingSpinner from "../../components/LoadingSpinner";
import EmptyComponent from "../../components/EmptyComponent";
import profileImage from "../../image/profile.png";

//* Sum a field across order groups, honouring per-currency totals.
const sumField = (groups, field) =>
  (groups || []).reduce((acc, g) => acc + (Number(g?.[field]) || 0), 0);

const Money = ({ kip, baht, usd, bold }) => {
  const parts = [];
  if (kip) parts.push(`${thousandSeperate(kip)} ກີບ`);
  if (baht) parts.push(`${thousandSeperate(baht)} ບາດ`);
  if (usd) parts.push(`${thousandSeperate(usd)} ໂດລາ`);
  if (parts.length === 0) parts.push("0 ກີບ");
  return (
    <span style={{ fontWeight: bold ? "bold" : "normal" }}>
      {parts.map((p, i) => (
        <div key={i}>{p}</div>
      ))}
    </span>
  );
};

const statusColor = (status) => {
  if (status === "CANCELLED" || status === "REJECT") return "#dc3545";
  if (status === "COMPLETED" || status === "SHIPPING_COMPLETED") return "#28a745";
  if (status === "CREATED" || status === "WAITINGCONFIRM") return "#fd7e14";
  return "#6c757d";
};

//* Friendly label + colour per origin channel. Falls back to the order's raw
//* `type` when `platform` is absent on older records.
//* Platform hues are kept in a purple/blue/teal/pink family that the status
//* palette (red/green/orange/grey) never uses, and platform badges render as a
//* light OUTLINED chip so they read as a category tag next to the solid status
//* badge — the two are no longer the same blue block (see screenshot).
const PLATFORM_META = {
  CF: { label: "CF", color: "#6f42c1" },
  SALE_PAGE: { label: "ເຊວເພຈ", color: "#0d6efd" },
  POS: { label: "POS", color: "#0dcaf0" },
  AFFILIATE: { label: "ອາຟຟີລີເອດ", color: "#d63384" },
};
const platformMeta = (value) =>
  PLATFORM_META[value] || (value ? { label: value, color: "#6f42c1" } : null);

//* Outlined chip style for a platform badge, given its brand colour.
const platformChipStyle = (color) => ({
  marginLeft: 6,
  background: "#fff",
  color,
  border: `1px solid ${color}`,
  fontWeight: 600,
});

function CustomerCFDetail() {
  const { history, match, location } = useReactRouter();
  const routeParam = decodeURIComponent(match.params.userFbId || "");
  const SHOP_ID = localStorage.getItem("SHOP");
  const passed = location?.state || {};

  //* The list routes CF customers by userFbId and Sale-page customers (no FB id)
  //* by a "phone:<number>" param. Build the order-history filter from whichever
  //* identity we actually have, preferring the explicit values passed in state.
  const identity = (() => {
    if (passed.userFbId) return { userFbId: passed.userFbId };
    if (routeParam.startsWith("phone:")) return { phone: routeParam.slice("phone:".length) };
    if (passed.phone) return { phone: passed.phone };
    return { userFbId: routeParam };
  })();
  const identityKey = identity.userFbId || identity.phone || "";

  const [groups, setGroups] = useState([]);
  const [openId, setOpenId] = useState(null);

  const [getHistory, { data, loading }] = useLazyQuery(GET_CUSTOMER_ORDER_HISTORY, {
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    getHistory({
      variables: {
        where: { shop: SHOP_ID, ...identity },
        orderBy: "createdAt_DESC",
        limit: 200,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey]);

  useEffect(() => {
    if (data?.orderGroups) setGroups(data.orderGroups.data || []);
  }, [data]);

  const header = groups[0] || {};
  const cfName = passed.cfName || header.cfName || header.customerName || "-";
  const phone = passed.phone || header.phone || "-";
  const image = passed.customerImage || header.customerImage || profileImage;

  const totalKip = sumField(groups, "totalPrice");
  const totalBaht = sumField(groups, "sumPriceBaht");
  const totalUsd = sumField(groups, "sumPriceUsd");
  const totalItems = sumField(groups, "amount");
  const paidCount = (groups || []).filter(
    (g) => g.isPaymented || ["COMPLETED", "PAID", "SUCCESS"].includes(g.status)
  ).length;

  return (
    <div>
      <div style={{ height: 10 }} />
      <button
        className="btn"
        style={{ color: COLOR_APP, paddingLeft: 0 }}
        onClick={() => history.goBack()}
      >
        <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 6 }} />
        ກັບຄືນ
      </button>

      {/* ===== Customer summary card ===== */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #eee",
          borderRadius: 8,
          padding: 18,
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
        }}
      >
        <Image
          src={image}
          onError={(e) => (e.target.src = profileImage)}
          style={{ height: 70, width: 70, borderRadius: "50%", objectFit: "cover" }}
        />
        <div style={{ flex: 1, minWidth: 200 }}>
          <h4 style={{ margin: 0, color: "#333" }}>{cfName}</h4>
          <div style={{ color: "#777" }}>ເບີໂທ: {phone}</div>
          {identity.userFbId && (
            <div style={{ color: "#aaa", fontSize: 12 }}>FB ID: {identity.userFbId}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div>
            <div style={{ color: "#999", fontSize: 12 }}>ຈຳນວນບິນ</div>
            <div style={{ fontSize: 20, fontWeight: "bold", color: COLOR_APP }}>
              {thousandSeperate(groups.length)}
            </div>
          </div>
          <div>
            <div style={{ color: "#999", fontSize: 12 }}>ສິນຄ້າລວມ</div>
            <div style={{ fontSize: 20, fontWeight: "bold", color: COLOR_APP }}>
              {thousandSeperate(totalItems)}
            </div>
          </div>
          <div>
            <div style={{ color: "#999", fontSize: 12 }}>ຈ່າຍແລ້ວ / ຄ້າງ</div>
            <div style={{ fontSize: 20, fontWeight: "bold" }}>
              <span style={{ color: "green" }}>{paidCount}</span>
              {" / "}
              <span style={{ color: "red" }}>{groups.length - paidCount}</span>
            </div>
          </div>
          <div>
            <div style={{ color: "#999", fontSize: 12 }}>ຍອດສັ່ງຊື້ລວມ</div>
            <Money kip={totalKip} baht={totalBaht} usd={totalUsd} bold />
          </div>
        </div>
      </div>

      <div style={{ height: 20 }} />
      <h5 style={{ color: "#616161" }}>
        <FontAwesomeIcon icon={faReceipt} style={{ marginRight: 8 }} />
        ປະຫວັດການສັ່ງຊື້ ({groups.length} ບິນ)
      </h5>

      {loading ? (
        <LoadingSpinner />
      ) : groups.length === 0 ? (
        <EmptyComponent title={"ລູກຄ້າຄົນນີ້ຍັງບໍ່ມີປະຫວັດການສັ່ງຊື້"} />
      ) : (
        groups.map((g) => {
          const isOpen = openId === g.id;
          return (
            <div
              key={g.id}
              style={{
                border: "1px solid #eee",
                borderRadius: 8,
                marginBottom: 12,
                overflow: "hidden",
              }}
            >
              {/* bill header row (clickable to expand items) */}
              <div
                className="hoverme"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  background: "#fafafa",
                  cursor: "pointer",
                  gap: 8,
                }}
                onClick={() => setOpenId(isOpen ? null : g.id)}
              >
                <div>
                  <b style={{ color: COLOR_APP }}>#{g.code || g.id}</b>
                  <span style={{ color: "#999", marginLeft: 10, fontSize: 13 }}>
                    {moment(g.createdAt).format("DD/MM/YYYY HH:mm")}
                  </span>
                  <Badge
                    bg=""
                    style={{
                      marginLeft: 10,
                      background: statusColor(g.status),
                      color: "#fff",
                    }}
                  >
                    {convertOrderStatus(g.status)}
                  </Badge>
                  {platformMeta(g.platform || g.type) && (
                    <Badge
                      bg=""
                      style={platformChipStyle(platformMeta(g.platform || g.type).color)}
                    >
                      {platformMeta(g.platform || g.type).label}
                    </Badge>
                  )}
                  <Badge
                    bg=""
                    style={{
                      marginLeft: 6,
                      background: g.isPaymented ? "#198754" : "#dc3545",
                      color: "#fff",
                    }}
                  >
                    {g.isPaymented ? "ຈ່າຍແລ້ວ" : "ຍັງບໍ່ຈ່າຍ"}
                  </Badge>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Money kip={g.totalPrice} baht={g.sumPriceBaht} usd={g.sumPriceUsd} bold />
                  <div style={{ fontSize: 12, color: "#999" }}>
                    {g.orders?.length || 0} ລາຍການ · {thousandSeperate(g.amount)} ຊິ້ນ
                  </div>
                </div>
              </div>

              {/* expanded product line items */}
              {isOpen && (
                <Table responsive size="sm" style={{ margin: 0 }}>
                  <thead>
                    <tr style={{ background: "#fff" }}>
                      <th>#</th>
                      <th>ລະຫັດ CF</th>
                      <th>ສິນຄ້າ</th>
                      <th className="text-center">ຈຳນວນ</th>
                      <th className="text-right">ລາຄາ/ໜ່ວຍ</th>
                      <th className="text-right">ລາຄາລວມ</th>
                      <th>ວັນທີ CF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(g.orders || []).map((o, i) => (
                      <tr key={o.id || i}>
                        <td>{i + 1}</td>
                        <td>
                          <FontAwesomeIcon
                            icon={faBoxOpen}
                            style={{ color: COLOR_APP, marginRight: 6 }}
                          />
                          {o.cfMessage || "-"}
                        </td>
                        <td>{o.productName || "-"}</td>
                        <td className="text-center">{thousandSeperate(o.amount)}</td>
                        <td className="text-right">
                          {thousandSeperate(o.price)} {o.currency || "ກີບ"}
                        </td>
                        <td className="text-right">
                          {thousandSeperate(o.totalPrice)} {o.currency || "ກີບ"}
                        </td>
                        <td>{moment(o.createdAt).format("DD/MM/YYYY HH:mm")}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          );
        })
      )}
      <div style={{ height: 40 }} />
    </div>
  );
}

export default CustomerCFDetail;
